import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  createSupplierThunk,
  deactivateSupplierThunk,
  loadSuppliers,
  setFilterStatus,
  setPage,
  setSearch,
  updateSupplierThunk,
} from '../features/suppliers/suppliersSlice';
import type { Supplier, SupplierPayload, SupplierStatus } from '../features/suppliers/types';
import { RootState, AppDispatch } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { SupplierForm } from '../components/SupplierForm';
import { DateCell } from '../components/DateCell';
import {
  REEVALUATION_STATE_CLASS,
  REEVALUATION_STATE_LABEL,
  resolveReevaluationState,
} from '../features/suppliers/supplierReevaluationUtils';

const STATUS_LABEL: Record<SupplierStatus, string> = {
  apto: 'Apto',
  no_apto: 'No apto',
  suspendido: 'Suspendido',
  baja: 'Baja',
};

const STATUS_CLASS: Record<SupplierStatus, string> = {
  apto: 'bg-green-100 text-green-800',
  no_apto: 'bg-red-100 text-red-800',
  suspendido: 'bg-amber-100 text-amber-800',
  baja: 'bg-zinc-200 text-zinc-700',
};

export function SuppliersPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('suppliers.access');
  const { items, loading, pagination, search, filterStatus, sortBy, sortDir } = useSelector(
    (s: RootState) => s.suppliers,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      loadSuppliers({
        search,
        status: filterStatus || undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy,
        sortDir,
      }),
    );
  }, [dispatch, search, filterStatus, pagination.page, pagination.pageSize, sortBy, sortDir]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((s: Supplier) => {
    setEditing(s);
    setDialogOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (payload: SupplierPayload) => {
      setSubmitting(true);
      try {
        if (editing) {
          await dispatch(updateSupplierThunk({ id: editing.id, payload })).unwrap();
        } else {
          await dispatch(createSupplierThunk(payload)).unwrap();
        }
        setDialogOpen(false);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, editing],
  );

  const handleDeactivate = useCallback(
    async (s: Supplier) => {
      const ok = await confirm({
        title: 'Dar de baja',
        message: `¿Dar de baja a "${s.legalName}"?`,
        confirmText: 'Dar de baja',
        variant: 'danger',
      });
      if (!ok) return;
      await dispatch(deactivateSupplierThunk(s.id));
    },
    [confirm, dispatch],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Proveedores</h1>
        {canEdit && (
          <button
            onClick={openCreate}
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
          >
            + Nuevo proveedor
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por razón social, CUIT, contacto…"
          className="border rounded-md px-3 py-2 flex-1 min-w-[240px]"
          value={search}
          onChange={(e) => dispatch(setSearch(e.target.value))}
        />
        <select
          className="border rounded-md px-3 py-2"
          value={filterStatus}
          onChange={(e) => dispatch(setFilterStatus(e.target.value as SupplierStatus | ''))}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Razón social</Th>
              <Th>CUIT</Th>
              <Th>Categoría</Th>
              <Th>Estado</Th>
              <Th>Próx. reevaluación</Th>
              <Th>Evaluaciones</Th>
              <Th>Alta</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">
                  No hay proveedores
                </td>
              </tr>
            )}
            {!loading &&
              items.map((s) => {
                const reevaluationState = resolveReevaluationState(s.nextReevaluationAt);
                return (
                <tr key={s.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2">
                    <Link to={`/proveedores/${s.id}`} className="text-primary hover:underline">
                      {s.legalName}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{s.taxId ?? '—'}</td>
                  <td className="px-3 py-2">{s.category ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_CLASS[s.status]}`}>
                      {STATUS_LABEL[s.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {s.nextReevaluationAt ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <DateCell value={s.nextReevaluationAt} />
                        {reevaluationState && (
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs ${REEVALUATION_STATE_CLASS[reevaluationState]}`}
                          >
                            {REEVALUATION_STATE_LABEL[reevaluationState]}
                          </span>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">{s._count?.evaluations ?? 0}</td>
                  <td className="px-3 py-2">
                    <DateCell value={s.createdAt} />
                  </td>
                  <td className="px-3 py-2">
                    {canEdit && (
                      <div className="flex gap-2">
                        <button
                          className="text-xs rounded bg-secondary px-2 py-1 hover:bg-secondary/80"
                          onClick={() => openEdit(s)}
                        >
                          Editar
                        </button>
                        {s.status !== 'baja' && (
                          <button
                            className="text-xs rounded bg-destructive text-destructive-foreground px-2 py-1 hover:bg-destructive/90"
                            onClick={() => handleDeactivate(s)}
                          >
                            Dar de baja
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
              })}
          </tbody>
        </table>
      </div>

      <Pager
        page={pagination.page}
        pages={pagination.pages}
        onChange={(p) => dispatch(setPage(p))}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
          </DialogHeader>
          <SupplierForm
            initial={editing}
            loading={submitting}
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
            readOnly={!canEdit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Pager({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="flex justify-end gap-2">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Anterior
      </button>
      <span className="px-3 py-1 text-sm">
        Página {page} de {pages}
      </span>
      <button
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Siguiente
      </button>
    </div>
  );
}
